import { animate, state, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';

import { BroadcastService } from '../../../../services/broadcast.service';
import { createLogger } from '../../../../utils/logger.util';
import { getColorById, isDevice, isUrl } from '../../../../utils/utils';
import { AppIconComponent } from '../../../icon/icon.component';
import { CALL_INCOMING_STATUS, CALL_STATE, CALL_TYPE } from '../../constants';
import type { DragPosition } from '../../directives/draggable.directive';
import { DraggableDirective } from '../../directives/draggable.directive';
import type { ResizeResult } from '../../directives/resizable.directive';
import { ResizableDirective } from '../../directives/resizable.directive';
import { CallStoreService } from '../../services/call-store.service';
import { PipService } from '../../services/pip.service';
import { CallActiveComponent } from '../call-active/call-active.component';
import { CallInvitationComponent } from '../call-invitation/call-invitation.component';

interface ModalState {
  x: number;
  y: number;
  width: number;
  height: number;
  isFullscreen: boolean;
}

const MIN_WIDTH = 400;
const MIN_HEIGHT = 600;

@Component({
  selector: 'app-call-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DraggableDirective,
    ResizableDirective,
    CallInvitationComponent,
    AppIconComponent,
    CallActiveComponent,
  ],
  templateUrl: './call-modal.component.html',
  styleUrl: './call-modal.component.scss',
  animations: [
    trigger('modalAnimation', [
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'scale(1)',
          filter: 'blur(0)',
          visibility: 'visible',
        }),
      ),
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'scale(0.4)',
          filter: 'blur(4px)',
          visibility: 'hidden',
        }),
      ),
      transition('void => visible', [
        style({
          opacity: 0,
          transform: 'scale(0.82)',
          filter: 'blur(10px)',
        }),
        animate('420ms cubic-bezier(0.22, 1, 0.36, 1)'),
      ]),
      transition('visible => hidden', [animate('180ms ease-in')]),
      transition('hidden => visible', [animate('420ms cubic-bezier(0.22, 1, 0.36, 1)')]),
      transition(':leave', [
        animate(
          '180ms ease-in',
          style({
            opacity: 0,
            transform: 'scale(0.4)',
            filter: 'blur(4px)',
          }),
        ),
      ]),
    ]),
  ],
})
export class CallModalComponent implements OnDestroy {
  private readonly logger = createLogger('Call - CallModalComponent');
  callStore = inject(CallStoreService);
  protected pipService = inject(PipService);
  private toastr = inject(ToastrService);
  private broadcastService = inject(BroadcastService);
  private platform = inject(PLATFORM_ID);

  readonly currentCall = this.callStore.currentCall;
  readonly callState = this.callStore.callState;
  readonly pendingInvitation = this.callStore.pendingInvitation;
  readonly isInCall = this.callStore.isInCall;
  readonly pendingParticipants = this.callStore.pendingParticipants;
  readonly activeParticipants = this.callStore.participants;
  readonly localMediaPermissionService = this.callStore.localMediaPermissionService;
  readonly isAnyScreenSharing = this.callStore.isAnyScreenSharing;

  CALL_STATE = CALL_STATE;

  isInMeeting = signal(false);

  readonly displayParticipant = computed(() => {
    if (this.pendingInvitation()) return this.pendingParticipants()[0] ?? null;
    return this.activeParticipants()[0] ?? null;
  });

  isDevice = computed(() => isDevice(this.displayParticipant()?.userId));
  isWaiting = computed(() => this.pendingInvitation()?.status === CALL_INCOMING_STATUS.WAITING);

  readonly isVideoCall = computed(
    () => this.callState() === CALL_STATE.CONNECTED && this.currentCall()?.type === CALL_TYPE.VIDEO,
  );

  readonly avatarBgError = signal(false);

  readonly hasValidAvatar = computed(() => {
    const avatar = this.displayParticipant()?.avatar;
    if (!avatar || typeof avatar !== 'string') return false;
    const trimmed = avatar.trim();
    if (trimmed.length === 0) return false;
    return isUrl(trimmed) || trimmed.startsWith('/');
  });

  private readonly hasLoadedAvatar = computed(() => this.hasValidAvatar() && !this.avatarBgError());

  readonly backgroundImage = computed(() => {
    if (this.isVideoCall()) return null;
    if (this.hasLoadedAvatar()) return `url(${this.displayParticipant()!.avatar})`;
    if (this.isDevice()) return 'url(assets/images/default_device.png)';
    return null;
  });

  readonly backgroundColor = computed(() => {
    if (this.hasLoadedAvatar()) return null;
    if (this.isDevice()) return 'var(--color-bg-hover)';
    return getColorById(this.displayParticipant()?.userId ?? '')[2];
  });

  readonly shapesColor = computed(() => {
    if (this.hasLoadedAvatar()) return null;
    return getColorById(this.displayParticipant()?.userId ?? '')[3];
  });

  private readonly pipArea = viewChild<ElementRef<HTMLElement>>('pipAreaRef');
  private readonly resizable = viewChild.required(ResizableDirective);
  private endedTimeoutId: ReturnType<typeof setTimeout> | null = null;

  protected readonly modal = signal<ModalState>({
    x: 0,
    y: 0,
    width: MIN_WIDTH,
    height: MIN_HEIGHT,
    isFullscreen: false,
  });

  constructor() {
    effect(
      () => {
        if (this.callState() === CALL_STATE.IDLE && !this.pendingInvitation()) {
          if (this.pipService.isOpen()) {
            this.pipService.closePip();
          }
          this.centerModal();
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const isPipOpen = this.pipService.isOpen();
        untracked(() => {
          if (!isPipOpen && this.callStore.isModalHidden()) {
            this.callStore.isModalHidden.set(false);
          }
        });
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const state = this.callState();
      if (this.endedTimeoutId) {
        clearTimeout(this.endedTimeoutId);
        this.endedTimeoutId = null;
      }

      if (state === CALL_STATE.ENDED) {
        this.endedTimeoutId = setTimeout(() => {
          if (this.callState() === CALL_STATE.ENDED) {
            // nếu còn cuộc gọi chờ thì chỉ xóa call hiện tại, giữ WAITING.
            if (this.pendingInvitation()) {
              this.callStore.reset(true);
            } else {
              this.handleClose();
            }
          }
        }, 5000);
      }
    });

    afterNextRender(() => {
      this.centerModal();
    });

    afterNextRender(() => {
      document.addEventListener('keydown', this.handleKeydown, true);
    });

    effect(() => {
      if (!this.pendingInvitation()) return;
      this.broadcastService.checkIsInMeeting().then((inMeeting) => this.isInMeeting.set(inMeeting));
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeydown, true);
    if (this.endedTimeoutId) {
      clearTimeout(this.endedTimeoutId);
      this.endedTimeoutId = null;
    }
  }

  handleCloseBtn() {
    switch (this.callState()) {
      case CALL_STATE.ENDED: {
        this.handleClose();
        break;
      }
      default:
        this.callStore.isModalHidden.update((v) => !v);
        break;
    }
  }

  async acceptCall(callId: string): Promise<void> {
    const checkPermission =
      await this.localMediaPermissionService.requestCameraMicPermission('mic');
    if (!checkPermission) return;
    try {
      if (this.isInMeeting()) {
        this.broadcastService.meeting({
          action: 'force-leave',
          payload: {},
        });
      }
      await this.callStore.callAccept(callId);
    } catch (error) {
      this.logger.error('Failed to accept call:', error);
    }
  }

  async rejectCall(callId: string): Promise<void> {
    try {
      const isRejectSwitchCall = this.pendingInvitation()?.status === CALL_INCOMING_STATUS.WAITING;
      await this.callStore.callReject(callId, isRejectSwitchCall);
    } catch (error) {
      this.logger.error('Failed to reject call:', error);
    }
  }

  handleClose() {
    this.callStore.reset();
    this.callStore.callSessionStore.setCurrentCallState(CALL_STATE.IDLE);
    this.callStore.isModalHidden.set(false);
    if (!this.pipService.isOpen()) return;
    this.pipService.closePip();
  }

  private readonly handleKeydown = (e: KeyboardEvent): void => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    if (e.key === 'Escape' && this.modal().isFullscreen) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      this.toggleFullscreen();
    }
  };

  private centerModal(): void {
    if (!isPlatformBrowser(this.platform)) return;
    const w = MIN_WIDTH;
    const h = MIN_HEIGHT;
    this.modal.set({
      width: w,
      height: h,
      isFullscreen: false,
      x: Math.max(0, (window.innerWidth - w) / 2),
      y: Math.max(0, (window.innerHeight - h) / 2.2),
    });
  }

  getResizable(): ResizableDirective {
    return this.resizable();
  }

  onDrag(pos: DragPosition): void {
    this.modal.update((s) => ({ ...s, x: pos.x, y: pos.y }));
  }

  onResize(result: ResizeResult): void {
    this.modal.update((s) => ({
      ...s,
      width: result.size.width,
      height: result.size.height,
      x: result.position.x,
      y: result.position.y,
    }));
  }

  toggleFullscreen(): void {
    this.modal.update((s) => ({ ...s, isFullscreen: !s.isFullscreen }));
    this.resizable().setHandlesVisible(!this.modal().isFullscreen);
    // this.toastr.info(this.modal().isFullscreen ? 'Toàn màn hình' : 'Thoát toàn màn hình');
  }

  async togglePip(): Promise<void> {
    if (!this.pipService.isSupported) {
      this.toastr.info('Trình duyệt không hỗ trợ PiP');
      return;
    }
    const el = this.pipArea()?.nativeElement;
    if (!el) return;
    if (this.pipService.isOpen()) {
      return;
    } else {
      await this.pipService.openPip(el, 360, 540);
      // this.toastr.info('Đã mở Picture-in-Picture');
      this.callStore.isModalHidden.set(true);
    }
  }

  onBackdropClick(e: MouseEvent): void {
    e.stopPropagation();
  }

  rejectCallTimeout() {
    if (this.pendingInvitation()) {
      this.callStore.showMissingCallNotification();
      this.callStore.callSessionStore.setPendingInvitation(null);
    }
  }
}
